import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { Download, Save, Trash2, Upload, ShieldCheck } from 'lucide-react';
import { usePortalI18n } from '../../context/PortalI18nContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KYC_CATEGORY_KEYS = [
  'gst_certificate', 'pan_card', 'msme_certificate', 'cancelled_cheque', 'other',
];

const PortalKYC = () => {
  const { division, refreshDivision } = useOutletContext();
  const { t, lang } = usePortalI18n();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [bank, setBank] = useState({
    pan: '', gstin: '', bank_account_number: '', ifsc_code: '', address: '', contact_phone: '',
  });
  const [savingBank, setSavingBank] = useState(false);

  const [uploadCategory, setUploadCategory] = useState('pan_card');
  const [uploading, setUploading] = useState(false);

  const catLabel = (slug) => t(`kyc.cat.${slug}`) || slug;

  const fetchKyc = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/portal/kyc`, { withCredentials: true });
      setDocs(res.data.kyc_documents || []);
      const d = res.data.division || {};
      setBank({
        pan: d.pan || '', gstin: d.gstin || '',
        bank_account_number: d.bank_account_number || '',
        ifsc_code: d.ifsc_code || '',
        address: d.address || '',
        contact_phone: d.contact_phone || '',
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || t('kyc.uploadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchKyc(); /* eslint-disable-next-line */ }, []);

  const saveBank = async (e) => {
    e.preventDefault();
    setSavingBank(true);
    try {
      await axios.put(`${API}/portal/division`, bank, { withCredentials: true });
      toast.success(t('kyc.saved'));
      await refreshDivision();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('kyc.saveFailed'));
    } finally {
      setSavingBank(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(
        `${API}/portal/kyc?category=${uploadCategory}`, fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      toast.success(t('kyc.uploaded'));
      await fetchKyc();
      await refreshDivision();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('kyc.uploadFailed'));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const downloadDoc = async (d) => {
    try {
      const res = await axios.get(`${API}/portal/kyc/${d.id}/download`, {
        responseType: 'blob', withCredentials: true,
      });
      const blob = new Blob([res.data], { type: d.content_type || res.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = d.filename;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error(t('kyc.downloadFailed'));
    }
  };

  const deleteDoc = async (d) => {
    if (!window.confirm(t('common.confirmRemove', { filename: d.filename }))) return;
    try {
      await axios.delete(`${API}/portal/kyc/${d.id}`, { withCredentials: true });
      toast.success(t('kyc.removed'));
      fetchKyc();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('kyc.deleteFailed'));
    }
  };

  const dateLocale = lang === 'hi' ? 'hi-IN' : 'en-IN';

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
          <ShieldCheck className="h-6 w-6 text-emerald-700" />
          {t('kyc.title')}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">{t('kyc.subtitle')}</p>
      </div>

      <Card>
        <CardHeader className="border-b border-border/50">
          <CardTitle className="text-base font-heading">{t('kyc.profileTitle')}</CardTitle>
          <CardDescription>{t('kyc.profileDesc')}</CardDescription>
        </CardHeader>
        <CardContent className="pt-5">
          <form onSubmit={saveBank} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t('kyc.field.name')}</Label>
                <Input value={division.name} readOnly className="bg-muted/40" />
              </div>
              <div className="space-y-1.5">
                <Label>{t('kyc.field.code')}</Label>
                <Input value={division.code} readOnly className="bg-muted/40 font-mono" />
              </div>
              <div className="space-y-1.5">
                <Label>{t('kyc.field.contactEmail')}</Label>
                <Input value={division.contact_email} readOnly className="bg-muted/40" />
              </div>
              <div className="space-y-1.5">
                <Label>{t('kyc.field.contactPhone')}</Label>
                <Input value={bank.contact_phone} onChange={(e) => setBank({ ...bank, contact_phone: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>{t('kyc.field.pan')}</Label>
                <Input value={bank.pan} onChange={(e) => setBank({ ...bank, pan: e.target.value.toUpperCase() })} placeholder="AAAAA0000A" />
              </div>
              <div className="space-y-1.5">
                <Label>{t('kyc.field.gstin')}</Label>
                <Input value={bank.gstin} onChange={(e) => setBank({ ...bank, gstin: e.target.value.toUpperCase() })} placeholder="23AAAAA0000A1Z5" />
              </div>
              <div className="space-y-1.5">
                <Label>{t('kyc.field.bankAccount')}</Label>
                <Input value={bank.bank_account_number} onChange={(e) => setBank({ ...bank, bank_account_number: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>{t('kyc.field.ifsc')}</Label>
                <Input value={bank.ifsc_code} onChange={(e) => setBank({ ...bank, ifsc_code: e.target.value.toUpperCase() })} />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>{t('kyc.field.address')}</Label>
                <Input value={bank.address} onChange={(e) => setBank({ ...bank, address: e.target.value })} />
              </div>
            </div>
            <div className="flex justify-end pt-2">
              <Button type="submit" disabled={savingBank} data-testid="save-bank-button">
                <Save className="h-4 w-4 mr-2" />
                {savingBank ? t('common.saving') : t('common.save')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="border-b border-border/50">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="text-base font-heading">{t('kyc.docsTitle')}</CardTitle>
              <CardDescription>{t('kyc.docsDesc')}</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Select value={uploadCategory} onValueChange={setUploadCategory}>
                <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {KYC_CATEGORY_KEYS.map((c) => (
                    <SelectItem key={c} value={c}>{catLabel(c)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <label>
                <Button asChild disabled={uploading} data-testid="upload-kyc-button">
                  <span className="cursor-pointer">
                    <Upload className={`h-4 w-4 mr-2 ${uploading ? 'animate-pulse' : ''}`} />
                    {uploading ? t('common.uploading') : t('common.upload')}
                  </span>
                </Button>
                <input type="file" className="hidden" accept="application/pdf,image/*" onChange={handleUpload} />
              </label>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          {loading ? (
            <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
          ) : docs.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">
              {t('kyc.empty')}<em>{t('common.upload')}</em>.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{lang === 'hi' ? 'श्रेणी' : 'Category'}</TableHead>
                  <TableHead>{t('common.filename')}</TableHead>
                  <TableHead>{t('common.size')}</TableHead>
                  <TableHead>{t('common.uploaded')}</TableHead>
                  <TableHead className="text-right">{t('common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {docs.map((d) => (
                  <TableRow key={d.id} data-testid={`kyc-row-${d.id}`}>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">{catLabel(d.category)}</Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{d.filename}</TableCell>
                    <TableCell className="tabular-nums text-xs">
                      {d.size_bytes ? `${Math.round(d.size_bytes / 1024)} KB` : '—'}
                    </TableCell>
                    <TableCell className="text-xs">
                      {d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString(dateLocale) : '—'}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => downloadDoc(d)} title={t('common.download')}>
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteDoc(d)} title={t('common.remove')}>
                        <Trash2 className="h-4 w-4 text-rose-600" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PortalKYC;
